import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "../ThemeProvider";
import { loadLeaflet, createLeafletCostasMap } from "../../services/leafletMapFallback";

function modalSize() {
  return {
    w: Math.round(window.innerWidth * 0.82),
    h: Math.round(window.innerHeight * 0.82),
  };
}

function modalOrigin(size) {
  return {
    x: Math.max(8, Math.round((window.innerWidth - size.w) / 2)),
    y: Math.max(16, Math.round((window.innerHeight - size.h) / 2)),
  };
}

function useDraggable(initialPos) {
  const [pos, setPos] = useState(initialPos);
  const drag = useRef(null);

  const onDragStart = useCallback(
    (e) => {
      if (e.button !== 0 || e.target.closest("button, a, input")) return;
      drag.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: pos.x,
        origY: pos.y,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pos.x, pos.y]
  );

  const onDragMove = useCallback((e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startX;
    const dy = e.clientY - drag.current.startY;
    setPos({
      x: Math.max(0, drag.current.origX + dx),
      y: Math.max(0, drag.current.origY + dy),
    });
  }, []);

  const onDragEnd = useCallback(() => {
    drag.current = null;
  }, []);

  return { pos, onDragStart, onDragMove, onDragEnd };
}

/**
 * Visor interactivo DPMT / servidumbres (WMS MITECO) centrado en la propiedad.
 * Pan + zoom (lupa +/−); no es foto estática.
 */
export default function ModalVisorCostas({
  open,
  onClose,
  lat,
  lng,
  referencia = "",
  label = "",
}) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const [size] = useState(modalSize);
  const { pos, onDragStart, onDragMove, onDragEnd } = useDraggable(modalOrigin(size));
  const mapBoxRef = useRef(null);
  const mapApiRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(18);

  const la = Number(lat);
  const ln = Number(lng);
  const hasCoords = Number.isFinite(la) && Number.isFinite(ln);
  const pinLabel = [referencia, label].filter(Boolean).join(" · ") || "Vivienda";

  const destroyMap = useCallback(() => {
    if (mapApiRef.current) {
      try {
        mapApiRef.current.destroy();
      } catch {
        /* noop */
      }
      mapApiRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    destroyMap();
    onClose();
  }, [destroyMap, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  useEffect(() => {
    if (!open || !hasCoords || !mapBoxRef.current) return undefined;
    let cancelled = false;
    setStatus("loading");
    setError("");
    destroyMap();

    (async () => {
      try {
        await loadLeaflet();
        if (cancelled || !mapBoxRef.current) return;
        const api = createLeafletCostasMap(mapBoxRef.current, {
          lat: la,
          lng: ln,
          zoom: 18,
          label: pinLabel,
        });
        mapApiRef.current = api;
        setZoom(api.map.getZoom());
        api.map.on("zoomend", () => setZoom(api.map.getZoom()));
        setStatus("ready");
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "No se pudo cargar el mapa");
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
      destroyMap();
    };
  }, [open, hasCoords, la, ln, pinLabel, destroyMap]);

  const zoomBy = (delta) => {
    const map = mapApiRef.current?.map;
    if (!map) return;
    map.setZoom(map.getZoom() + delta);
  };

  const recenter = () => {
    const map = mapApiRef.current?.map;
    if (!map || !hasCoords) return;
    map.setView([la, ln], Math.max(map.getZoom(), 17));
  };

  if (!open) return null;

  const zoomBtn = {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1px solid ${t.border}`,
    background: isDark ? "rgba(15,23,42,0.92)" : "rgba(255,255,255,0.96)",
    color: t.textPrimary,
    fontSize: 20,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,0.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 220, pointerEvents: "none" }}>
      <div
        style={{
          pointerEvents: "auto",
          position: "absolute",
          left: pos.x,
          top: pos.y,
          width: size.w,
          height: size.h,
          display: "flex",
          flexDirection: "column",
          borderRadius: 12,
          overflow: "hidden",
          border: `1px solid ${t.border}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.4)",
          background: isDark ? "#0f172a" : "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            padding: "8px 12px",
            background: isDark ? "#1e293b" : "#f8fafc",
            borderBottom: `1px solid ${t.border}`,
            flexShrink: 0,
          }}
        >
          <div
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            style={{ flex: 1, cursor: "grab", userSelect: "none", minWidth: 0 }}
          >
            <div style={{ fontWeight: 700, fontSize: 14, color: t.textPrimary }}>
              🌊 Visor Costas {referencia ? `· Ref. ${referencia}` : ""}
            </div>
            <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2 }}>
              Arrastra el mapa · lupa +/− · informativo © MITECO + IGN · Esc cierra
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              background: isDark ? "#334155" : "#e2e8f0",
              fontSize: 16,
              width: 28,
              height: 28,
              cursor: "pointer",
              color: t.textPrimary,
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, position: "relative", minHeight: 0, background: "#0b1220" }}>
          {!hasCoords && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: t.textMuted,
                fontSize: 13,
                padding: 24,
                textAlign: "center",
              }}
            >
              Faltan coordenadas de la propiedad en Localización.
            </div>
          )}

          {hasCoords && <div ref={mapBoxRef} style={{ width: "100%", height: "100%" }} />}

          {status === "loading" && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(15,23,42,0.45)",
                color: "#e2e8f0",
                fontSize: 13,
                fontWeight: 600,
                zIndex: 500,
              }}
            >
              Cargando líneas de costas…
            </div>
          )}

          {error && (
            <div
              style={{
                position: "absolute",
                left: 12,
                bottom: 12,
                zIndex: 500,
                padding: "8px 12px",
                borderRadius: 8,
                background: "rgba(127,29,29,0.92)",
                color: "#fecaca",
                fontSize: 12,
                maxWidth: "70%",
              }}
            >
              {error}
            </div>
          )}

          {hasCoords && status === "ready" && (
            <div
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <button type="button" title="Ampliar" aria-label="Ampliar" onClick={() => zoomBy(1)} style={zoomBtn}>
                🔍+
              </button>
              <button type="button" title="Reducir" aria-label="Reducir" onClick={() => zoomBy(-1)} style={zoomBtn}>
                🔍−
              </button>
              <button type="button" title="Centrar en la propiedad" aria-label="Centrar" onClick={recenter} style={{ ...zoomBtn, fontSize: 14 }}>
                ◎
              </button>
              <div
                style={{
                  textAlign: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: isDark ? "#94a3b8" : "#64748b",
                  background: isDark ? "rgba(15,23,42,0.85)" : "rgba(255,255,255,0.9)",
                  borderRadius: 6,
                  padding: "2px 4px",
                }}
              >
                z{zoom}
              </div>
            </div>
          )}

          <div
            style={{
              position: "absolute",
              left: 10,
              bottom: 10,
              zIndex: 500,
              padding: "6px 10px",
              borderRadius: 8,
              background: isDark ? "rgba(15,23,42,0.88)" : "rgba(255,255,255,0.92)",
              border: `1px solid ${t.border}`,
              fontSize: 10,
              color: t.textMuted,
              maxWidth: 320,
              lineHeight: 1.35,
            }}
          >
            Capas: deslinde DPMT + serv. protección · datos informativos MITECO
          </div>
        </div>
      </div>
    </div>
  );
}
